import React, { useEffect, useState, cloneElement, lazy } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { FileTextIcon, CheckIcon, MailIcon, CalendarCheckIcon, RepeatIcon, TrendingUpIcon } from 'lucide-react';
// "Works where you work" visual — a real iPhone anchored to the BOTTOM of the
// card (its bottom rail + home indicator are in frame; the top runs off the top
// edge). On the white screen, a short Giga chat conversation plays on a loop:
// the user asks Giga to do real work, Giga acknowledges, and a result card
// appears — so the phone reads as a live demo of what Giga can do. Behind the
// phone, the task-pill marquee (Card1Background) drifts across the section.
// A wide, UNIQUE pool of tasks. The carousel is split into three rows that each
// get a DISTINCT slice of this pool, so no task ever appears in more than one
// row (no visible repeats across the field). Each carries the correct brand
// logo via its simpleicons slug.
type PhoneTask = {
  text: React.ReactNode;
  icon: string;
};
const PHONE_TASKS: PhoneTask[] = [{
  text: <>
        Organise my
        <br data-id="element-532" />
        meetings today
      </>,
  icon: 'googlecalendar'
}, {
  text: <>
        Build my
        <br data-id="element-533" />
        operations manual
      </>,
  icon: 'notion'
}, {
  text: <>
        Boost my
        <br data-id="element-534" />
        Shopify sales
      </>,
  icon: 'shopify'
}, {
  text: <>
        Improve my
        <br data-id="element-535" />
        website SEO
      </>,
  icon: 'semrush'
}, {
  text: <>
        Lower my
        <br data-id="element-536" />
        ad costs
      </>,
  icon: 'meta'
}, {
  text: <>
        Draft customer
        <br data-id="element-537" />
        outreach messages
      </>,
  icon: 'linkedin'
}, {
  text: <>
        Grow my
        <br data-id="element-538" />X following
      </>,
  icon: 'x'
}, {
  text: <>
        Manage my
        <br data-id="element-539" />
        cash flow
      </>,
  icon: 'quickbooks'
}, {
  text: <>
        Suggest ways to
        <br data-id="element-540" />
        grow revenue
      </>,
  icon: 'stripe'
}, {
  text: <>
        Respond to
        <br data-id="element-541" />
        my customers
      </>,
  icon: 'gmail'
}, {
  text: <>
        Update my
        <br data-id="element-542" />
        CRM records
      </>,
  icon: 'salesforce'
}, {
  text: <>
        Nurture my
        <br data-id="element-543" />
        sales pipeline
      </>,
  icon: 'hubspot'
}, {
  text: <>
        Schedule my
        <br data-id="element-544" />
        social posts
      </>,
  icon: 'buffer'
}, {
  text: <>
        Design my
        <br data-id="element-545" />
        next campaign
      </>,
  icon: 'canva'
}, {
  text: <>
        Send my
        <br data-id="element-546" />
        email newsletter
      </>,
  icon: 'mailchimp'
}, {
  text: <>
        Recap my
        <br data-id="element-547" />
        team's week
      </>,
  icon: 'slack'
}, {
  text: <>
        Triage my
        <br data-id="element-548" />
        support tickets
      </>,
  icon: 'zendesk'
}, {
  text: <>
        Reconcile my
        <br data-id="element-549" />
        monthly books
      </>,
  icon: 'xero'
}];
// Split the pool into three non-overlapping row slices.
const TASK_ROWS: PhoneTask[][] = (() => {
  const per = Math.ceil(PHONE_TASKS.length / 3);
  return [PHONE_TASKS.slice(0, per), PHONE_TASKS.slice(per, per * 2), PHONE_TASKS.slice(per * 2)];
})();
function TaskPill({
  task
}: {
  task: PhoneTask;
}) {
  return <div className="flex items-center gap-4 bg-white rounded-2xl px-4 py-3 shadow-sm border border-neutral-200/60 shrink-0" data-id="element-550">
      <span className="text-[13px] font-medium text-neutral-600 leading-snug" data-id="element-551">
        {task.text}
      </span>
      <img src={`https://cdn.simpleicons.org/${task.icon}`} alt="" className="w-5 h-5 object-contain shrink-0" loading="lazy" onError={e => {
      // Graceful fallback so a missing simpleicons slug never shows a
      // broken-image box: try the dashboard-icons CDN, then hide cleanly.
      const img = e.currentTarget as HTMLImageElement;
      if (!img.dataset.fallback) {
        img.dataset.fallback = '1';
        img.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${task.icon}.svg`;
      } else {
        img.style.visibility = 'hidden';
      }
    }} data-id="element-552" />
    </div>;
}
// The task carousel. Three rows, each a distinct slice of the task pool, drift
// at slightly different slow speeds and alternating directions. It fills the
// FULL width of its band edge-to-edge (no left fade) — the headline sits ABOVE
// this band, so the two never overlap.
const ROW_DIRECTIONS = [['-50%', '0%'], ['0%', '-50%'], ['-50%', '0%']] as const;
const ROW_DURATIONS = [80, 70, 90];
function Card1Background() {
  const reduced = !!useReducedMotion();
  return <div className="absolute inset-0 z-0 pointer-events-none flex flex-col justify-center gap-4 overflow-hidden" style={{
    // Soft gradient fade on the left and right edges so the pills dissolve
    // elegantly into the card instead of hard-cutting at the borders.
    maskImage: 'linear-gradient(to right, transparent 0%, #000 7%, #000 93%, transparent 100%)',
    WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 7%, #000 93%, transparent 100%)'
  }} data-id="element-553">
      {TASK_ROWS.map((row, r) => {
      // Duplicate the slice once for a seamless loop (the only repetition,
      // required by the marquee; no task is shared across rows).
      const loop = [...row, ...row];
      return <motion.div key={r} className="flex gap-4 w-max" animate={reduced ? undefined : {
        x: [ROW_DIRECTIONS[r][0], ROW_DIRECTIONS[r][1]]
      }} transition={reduced ? undefined : {
        duration: ROW_DURATIONS[r],
        repeat: Infinity,
        ease: 'linear'
      }} data-id="element-554">
            {loop.map((task, i) => <TaskPill key={i} task={task} data-id="element-555" />)}
          </motion.div>;
    })}
    </div>;
}
// A dynamic, premium group chat scene that demonstrates Giga's multiplayer
// capabilities. It features a conversation between a colleague (Marcus), the
// user (Faith), and Giga. The animation is snappy and fills the screen.
type ChatStep = {
  id: string;
  sender: 'marcus' | 'faith' | 'giga';
  text?: React.ReactNode;
  isResult?: boolean;
};
const CHAT_SCRIPT: ChatStep[] = [{
  id: '1',
  sender: 'marcus',
  text: 'Can someone pull the latest churn numbers for the board deck?'
}, {
  id: '2',
  sender: 'faith',
  text: <>
        <span className="font-semibold text-[#EA580C]" data-id="element-556">@Giga</span> grab those
        from Stripe and drop a summary here.
      </>
}, {
  id: '3',
  sender: 'giga',
  text: 'Done. Here is the churn summary for Q3.'
}, {
  id: '4',
  sender: 'giga',
  isResult: true
}];
// Giga's avatar — the brand flower mark in white on a solid Giga-orange circle,
// so the assistant always reads as Giga (not a generic AI sparkle).
function GigaAvatar({
  size = 24
}: {
  size?: number;
}) {
  return <span className="flex shrink-0 items-center justify-center rounded-full bg-[#EA580C]" style={{
    width: size,
    height: size
  }} data-id="element-557">
      <span className="bg-white" style={{
      width: size * 0.56,
      height: size * 0.56,
      WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
      WebkitMaskRepeat: 'no-repeat',
      maskRepeat: 'no-repeat',
      WebkitMaskPosition: 'center',
      maskPosition: 'center',
      WebkitMaskSize: 'contain',
      maskSize: 'contain'
    }} data-id="element-558" />
    </span>;
}
function TypingDots() {
  return <span className="flex items-center gap-1" data-id="element-559">
      {[0, 1, 2].map(i => <motion.span key={i} className="h-1.5 w-1.5 rounded-full bg-neutral-400" animate={{
      opacity: [0.4, 1, 0.4],
      y: [0, -3, 0]
    }} transition={{
      duration: 0.8,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: i * 0.15
    }} data-id="element-560" />)}
    </span>;
}
function ResultBubble() {
  return <div className="flex w-full max-w-[85%] items-center gap-3 rounded-2xl bg-[#EA580C] px-4 py-3 shadow-[0_12px_28px_-10px_rgba(234,88,12,0.65)]" data-id="element-561">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/20" data-id="element-562">
        <TrendingUpIcon size={20} strokeWidth={2.5} className="text-white" data-id="element-563" />
      </span>
      <span className="flex flex-col leading-tight" data-id="element-564">
        <span className="text-[14px] font-bold text-white" data-id="element-565">
          Q3 Churn Report
        </span>
        <span className="mt-0.5 text-[12px] font-medium text-white/90" data-id="element-566">
          3 accounts recovered
        </span>
      </span>
    </div>;
}
function PhoneChatScene() {
  const reduced = !!useReducedMotion();
  const [visible, setVisible] = useState(reduced ? CHAT_SCRIPT.length : 0);
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    if (reduced) return;
    let timers: ReturnType<typeof setTimeout>[] = [];
    let cancelled = false;
    const run = () => {
      setVisible(0);
      setTyping(false);
      let t = 800;
      // Marcus asks
      timers.push(setTimeout(() => {
        if (cancelled) return;
        setVisible(1);
      }, t));
      t += 1800;
      // Faith replies
      timers.push(setTimeout(() => {
        if (cancelled) return;
        setVisible(2);
      }, t));
      t += 1200;
      // Giga typing
      timers.push(setTimeout(() => {
        if (!cancelled) setTyping(true);
      }, t));
      t += 1400;
      // Giga replies
      timers.push(setTimeout(() => {
        if (cancelled) return;
        setTyping(false);
        setVisible(3);
      }, t));
      t += 800;
      // Giga result
      timers.push(setTimeout(() => {
        if (cancelled) return;
        setVisible(4);
      }, t));
      t += 2600;
      // Loop
      timers.push(setTimeout(() => {
        if (!cancelled) run();
      }, t));
    };
    const start = setTimeout(run, 400);
    return () => {
      cancelled = true;
      clearTimeout(start);
      timers.forEach(clearTimeout);
    };
  }, [reduced]);
  return <div className="flex h-full w-full flex-col bg-white" data-id="element-567">
      {/* Premium Chat Header */}
      <div className="flex items-center gap-3 border-b border-neutral-100 bg-white/80 px-5 pb-3 pt-5 backdrop-blur-md z-10" data-id="element-568">
        <div className="flex -space-x-2" data-id="element-569">
          <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[#E7DFD5] text-xs font-bold text-[#6B5D52] z-30" data-id="element-570">
            M
          </div>
          <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-white bg-[#FBEEC9] text-xs font-bold text-[#9A6B23] z-20" data-id="element-571">
            F
          </div>
          <div className="rounded-full border-2 border-white z-10" data-id="element-572">
            <GigaAvatar size={28} data-id="element-573" />
          </div>
        </div>
        <div data-id="element-574">
          <div className="text-[15px] font-bold text-neutral-900 leading-tight" data-id="element-575">
            Growth Team
          </div>
          <div className="text-[12px] font-medium text-neutral-500 leading-tight" data-id="element-576">
            3 members
          </div>
        </div>
      </div>

      {/* Conversation Area */}
      <div className="flex flex-1 flex-col justify-end px-4 pb-3" data-id="element-577">
        <div className="flex flex-col gap-4" data-id="element-578">
          <AnimatePresence initial={false} data-id="element-579">
            {CHAT_SCRIPT.slice(0, visible).map(step => {
            const isMarcus = step.sender === 'marcus';
            const isFaith = step.sender === 'faith';
            const isGiga = step.sender === 'giga';
            return <motion.div key={step.id} layout initial={reduced ? false : {
              opacity: 0,
              y: 15,
              scale: 0.95
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} transition={{
              type: 'spring',
              stiffness: 400,
              damping: 28
            }} className={`flex w-full ${isFaith ? 'justify-end' : 'justify-start'}`} data-id="element-580">
                  {isMarcus && <div className="flex items-end gap-2 max-w-[85%]" data-id="element-581">
                      <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#E7DFD5] text-[10px] font-bold text-[#6B5D52] mb-1" data-id="element-582">
                        M
                      </div>
                      <div className="rounded-2xl rounded-bl-sm bg-neutral-100 px-4 py-2.5 text-[14px] font-medium leading-snug text-neutral-700" data-id="element-583">
                        {step.text}
                      </div>
                    </div>}

                  {isFaith && <div className="max-w-[85%] rounded-2xl rounded-br-sm bg-[#FBEEC9] px-4 py-2.5 text-[14px] font-medium leading-snug text-neutral-900 shadow-sm" data-id="element-584">
                      {step.text}
                    </div>}

                  {isGiga && !step.isResult && <div className="flex items-end gap-2 max-w-[85%]" data-id="element-585">
                      <div className="mb-1" data-id="element-586">
                        <GigaAvatar size={24} data-id="element-587" />
                      </div>
                      <div className="rounded-2xl rounded-bl-sm border border-[#EA580C]/20 bg-white px-4 py-2.5 text-[14px] font-medium leading-snug text-neutral-800 shadow-sm" data-id="element-588">
                        {step.text}
                      </div>
                    </div>}

                  {isGiga && step.isResult && <div className="flex items-end gap-2 w-full" data-id="element-589">
                      <div className="w-6 shrink-0" data-id="element-590" />{' '}
                      {/* Spacer for avatar alignment */}
                      <ResultBubble data-id="element-591" />
                    </div>}
                </motion.div>;
          })}
          </AnimatePresence>

          {/* Typing indicator */}
          <AnimatePresence data-id="element-592">
            {typing && !reduced && <motion.div key="typing" layout initial={{
            opacity: 0,
            y: 10,
            scale: 0.95
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9,
            transition: {
              duration: 0.15
            }
          }} transition={{
            type: 'spring',
            stiffness: 400,
            damping: 28
          }} className="flex justify-start" data-id="element-593">
                <div className="flex items-end gap-2" data-id="element-594">
                  <div className="mb-1" data-id="element-595">
                    <GigaAvatar size={24} data-id="element-596" />
                  </div>
                  <div className="rounded-2xl rounded-bl-sm border border-[#EA580C]/20 bg-white px-4 py-3 shadow-sm" data-id="element-597">
                    <TypingDots data-id="element-598" />
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>

      {/* Message input bar — completes the true-to-life mobile chat screen,
            sitting just above the home indicator. */}
      <div className="flex items-center gap-2 px-4 pb-8 pt-1" data-id="element-599">
        <div className="flex flex-1 items-center rounded-full border border-neutral-200 bg-neutral-50 px-4 py-2.5" data-id="element-600">
          <span className="text-[14px] font-medium text-neutral-400" data-id="element-601">
            Message Growth Team
          </span>
        </div>
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#EA580C]" data-id="element-602">
          <MailIcon size={16} strokeWidth={2.5} className="text-white" data-id="element-603" />
        </span>
      </div>
    </div>;
}
function WorksWhereYouWorkVisual() {
  // A real-size iPhone (device width ~330px, true to life) anchored to the
  // BOTTOM-CENTER of the card. The device is much taller than the card, so its
  // TOP runs off the top edge and only its lower ~60% is in frame — the active
  // conversation, the input bar and the home indicator. Because the device is
  // rendered at (near) real dimensions and NOT scaled way down, the chat UI —
  // 15px bubble text, 17px header — reads at true-to-life mobile scale.
  return <div className="absolute inset-0 overflow-hidden" data-id="element-604">
      <div className="absolute left-1/2 -translate-x-1/2 bottom-[-18px] w-[330px] h-[680px]" data-id="element-605">
        <div className="relative h-full w-full rounded-[3.4rem] p-[4px] shadow-[0_24px_60px_-16px_rgba(43,33,26,0.5)]" style={{
        background: 'linear-gradient(150deg, #5A5A5E 0%, #232326 28%, #050505 60%, #34343A 100%)'
      }} data-id="element-606">
          <div className="relative h-full w-full rounded-[3.15rem] bg-black p-[10px]" data-id="element-607">
            {/* The screen */}
            <div className="relative flex h-full w-full flex-col rounded-[2.6rem] bg-white overflow-hidden" data-id="element-608">
              <PhoneChatScene data-id="element-609" />

              {/* Home indicator */}
              <div className="absolute bottom-0 inset-x-0 pb-2 pt-6 flex justify-center z-20 bg-gradient-to-t from-white via-white/80 to-transparent" data-id="element-610">
                <span className="h-1 w-32 rounded-full bg-black/80" data-id="element-611" />
              </div>
            </div>
          </div>
        </div>
        {/* Soft fade at the top edge where the device leaves the frame, so the
            crop reads as an intentional bleed rather than a hard cut. */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/85 to-transparent z-30" data-id="element-612" />
      </div>
    </div>;
}
// "Connects in seconds" visual — modeled on the uploaded reference: full-color
// app tiles on soft rounded-white chips, scattered across the field riding on
// faint dashed orbital arcs that all sweep toward the BOTTOM-RIGHT corner,
// where the Giga mark sits as the orbit's anchor (every tool orbiting in to
// Giga). Tiles vary in size for depth and float gently. Business + social
// tools Giga plugs into.
const CONNECT_TILES = [
// size: tile px. Positions are tuned for an EVEN, airy scatter across the
// whole field (roughly three loose rows) with gentle size variation for
// depth — they still drift toward the Giga hub in the lower-right, but the
// field reads balanced rather than clumped in the upper-left. The bottom-
// right corner is intentionally left light because the Giga hub sits there.
// A broad mix of the real SaaS / business tools founders and teams run on.
// — top row —
{
  slug: 'asana',
  name: 'Asana',
  size: 50,
  top: '6%',
  left: '8%',
  rotate: -6,
  delay: 0.2
}, {
  slug: 'shopify',
  name: 'Shopify',
  size: 56,
  top: '2%',
  left: '40%',
  rotate: -4,
  delay: 0
}, {
  slug: 'stripe',
  name: 'Stripe',
  size: 46,
  top: '8%',
  left: '74%',
  rotate: 6,
  delay: 0.5
},
// — middle row —
{
  slug: 'gmail',
  name: 'Gmail',
  size: 44,
  top: '34%',
  left: '24%',
  rotate: 4,
  delay: 0.9
}, {
  slug: 'salesforce',
  name: 'Salesforce',
  size: 52,
  top: '38%',
  left: '54%',
  rotate: -5,
  delay: 1.2
}, {
  slug: 'notion',
  name: 'Notion',
  size: 44,
  top: '32%',
  left: '84%',
  rotate: 5,
  delay: 0.7
},
// — bottom row —
{
  slug: 'quickbooks',
  name: 'QuickBooks',
  size: 50,
  top: '64%',
  left: '6%',
  rotate: 6,
  delay: 0.3
}, {
  slug: 'googlesheets',
  name: 'Google Sheets',
  size: 46,
  top: '68%',
  left: '36%',
  rotate: -4,
  delay: 1.4
}, {
  slug: 'hubspot',
  name: 'HubSpot',
  size: 44,
  top: '62%',
  left: '64%',
  rotate: 4,
  delay: 1.0
}, {
  slug: 'zendesk',
  name: 'Zendesk',
  size: 42,
  top: '46%',
  left: '40%',
  rotate: -5,
  delay: 0.6
}];
function ConnectTile({
  tile,
  reduced
}: {
  tile: (typeof CONNECT_TILES)[number];
  reduced: boolean;
}) {
  const radius = tile.size >= 60 ? 18 : tile.size >= 50 ? 15 : 12;
  return <motion.div className="absolute bg-white shadow-[0_16px_34px_-14px_rgba(43,33,26,0.30)] ring-1 ring-black/[0.04] flex items-center justify-center" style={{
    top: tile.top,
    left: tile.left,
    width: tile.size,
    height: tile.size,
    borderRadius: radius,
    rotate: `${tile.rotate}deg`
  }} initial={{
    opacity: 0,
    scale: 0.6
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: tile.delay * 0.25,
    type: 'spring',
    stiffness: 220,
    damping: 17
  }} data-id="element-613">
      <motion.img src={`https://cdn.simpleicons.org/${tile.slug}`} alt={tile.name} className="object-contain" style={{
      width: tile.size * 0.52,
      height: tile.size * 0.52
    }} loading="lazy" onError={e => {
      const el = e.currentTarget as HTMLImageElement;
      if (!el.dataset.fallback) {
        el.dataset.fallback = '1';
        el.src = `https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${tile.slug}.svg`;
      } else {
        el.style.visibility = 'hidden';
      }
    }} animate={reduced ? undefined : {
      y: [0, -6, 0]
    }} transition={reduced ? undefined : {
      duration: 4 + tile.delay,
      repeat: Infinity,
      ease: 'easeInOut',
      delay: tile.delay
    }} data-id="element-614" />
    </motion.div>;
}
// The Giga mark anchored in the bottom-right corner — the orbital centre that
// every app tile arcs toward. Kept STATIC and flat: no perpetual float and no
// glow/halo, per request, so it simply sits as the solid hub of the
// constellation (it still fades/scales in once on first reveal).
function ConnectsHub({
  reduced
}: {
  reduced: boolean;
}) {
  return <motion.div className="absolute -bottom-3 -right-3 z-10 flex items-center justify-center" initial={{
    opacity: 0,
    scale: 0.5
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} viewport={{
    once: true
  }} transition={{
    delay: 0.2,
    type: 'spring',
    stiffness: 200,
    damping: 16
  }} data-id="element-615">
      <div className="relative w-[68px] h-[68px] flex items-center justify-center" data-id="element-616">
        {/* The Giga flower on its own — no orange tile, no glow. The mark is
          filled with a FLAT brand orange (#EA580C, the same orange as the "Try
          Giga for free" buttons) using the PNG silhouette as a CSS mask, so it's
          a clean solid color with no gradient and no drop shadow. */}
        <span role="img" aria-label="Giga" className="w-12 h-12 bg-[#EA580C]" style={{
        WebkitMaskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
        maskImage: 'url(https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png)',
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskPosition: 'center',
        maskPosition: 'center',
        WebkitMaskSize: 'contain',
        maskSize: 'contain'
      }} data-id="element-617" />
      </div>
    </motion.div>;
}
function ConnectsVisual() {
  const reduced = !!useReducedMotion();
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-618">
      {/* Concentric dashed orbital arcs the tiles ride on — anchored to the
                                                                                                                                                                                                                BOTTOM-RIGHT corner (the Giga hub) so every arc sweeps inward toward
                                                                                                                                                                                                                it, matching the reference's converging-orbit composition. */}
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true" data-id="element-619">
        {[34, 54, 76, 98].map((r, i) => <circle key={r} cx="100" cy="104" r={r} fill="none" stroke="#EA580C" strokeOpacity={0.38 - i * 0.05} strokeWidth="0.9" strokeDasharray="1.6 2.4" vectorEffect="non-scaling-stroke" data-id="element-620" />)}
      </svg>
      {/* The absolutely-positioned tiles are laid out in % across the card. On
                                                                                                                                                                                  a narrow mobile card those fixed-px tiles crowd and collide, so we scale
                                                                                                                                                                                  the whole constellation down a touch on small screens (origin top-center)
                                                                                                                                                                                  so it stays airy and uncluttered; full size from sm up. */}
      <div className="absolute inset-0 origin-top scale-[0.82] sm:scale-100" data-id="element-621">
        {CONNECT_TILES.map(tile => <ConnectTile key={tile.slug} tile={tile} reduced={reduced} data-id="element-622" />)}
      </div>
      <ConnectsHub reduced={reduced} data-id="element-623" />
    </div>;
}
// "Acts like a partner" visual — a DENSE pill-cloud (modeled on the uploaded
// reference layout): a field of faint grey ghost pills (the endless busy-work)
// tilted and bleeding off the card edges, with three vivid accent pills
// floating in front — the work Giga actually picks up. The accent pills use
// ONLY Giga's palette (warm orange family + dark brown), not the reference's
// purple/pink/green, which aren't part of our system.
const GHOST_PILLS = [{
  text: 'tips',
  top: '6%',
  left: '40%',
  rotate: -4
}, {
  text: 'content',
  top: '2%',
  left: '72%',
  rotate: 5
}, {
  text: 'goals',
  top: '20%',
  left: '-8%',
  rotate: -7
}, {
  text: 'reals',
  top: '24%',
  left: '74%',
  rotate: 6
}, {
  text: 'statistics',
  top: '44%',
  left: '-6%',
  rotate: 4
}, {
  text: 'research',
  top: '46%',
  left: '82%',
  rotate: -5
}, {
  text: 'new ideas',
  top: '74%',
  left: '14%',
  rotate: -4
}, {
  text: 'captions',
  top: '70%',
  left: '-10%',
  rotate: 6
}, {
  text: 'recaps',
  top: '88%',
  left: '64%',
  rotate: 5
}];
const ACCENT_PILLS = [{
  text: 'replies',
  top: '20%',
  left: '14%',
  rotate: -6,
  bg: 'bg-[#F59E5B]',
  delay: 0
}, {
  text: 'nudges',
  top: '42%',
  left: '32%',
  rotate: 5,
  bg: 'bg-[#EA580C]',
  delay: 0.12
}, {
  text: 'notices',
  top: '64%',
  left: '46%',
  rotate: -5,
  bg: 'bg-[#C2410C]',
  delay: 0.24
}];
function PartnerVisual() {
  const reduced = !!useReducedMotion();
  return <div className="flex-1 relative mt-6 overflow-hidden" data-id="element-624">
      {/* The pill cloud is laid out with absolute % positions tuned for the
                                                                                                                                                                                  desktop card. On a narrow mobile card the bigger accent pills overlap
                                                                                                                                                                                  hard and a few ghost pills bleed messily off-frame, so we scale the
                                                                                                                                                                                  whole cloud down a touch on small screens (origin top-center) to keep
                                                                                                                                                                                  it legible and tidy; full size from sm up. */}
      <div className="absolute inset-0 origin-top scale-[0.8] sm:scale-100" data-id="element-625">
        {/* Ghost layer — dense, faint, tilted pills, several bleeding past the
          card edges so the cloud feels deeper and busier than the frame. */}
        {GHOST_PILLS.map((pill, i) => <motion.span key={pill.text} className="absolute whitespace-nowrap px-4 py-2 rounded-full text-sm font-bold text-neutral-300 bg-neutral-100" style={{
        top: pill.top,
        left: pill.left,
        rotate: `${pill.rotate}deg`
      }} initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: i * 0.04,
        duration: 0.4
      }} data-id="element-626">
            {pill.text}
          </motion.span>)}
        {/* Accent layer — the vivid pills Giga picks up: larger, bolder, and
          floating above the ghost field. */}
        {ACCENT_PILLS.map(pill =>
      // The whole pill floats; the text inside stays put relative to it.
      // The continuous bob lives on a dedicated wrapper so it doesn't fight
      // the entrance spring (initial/whileInView) on the same element.
      <motion.span key={pill.text} className="absolute z-10" style={{
        top: pill.top,
        left: pill.left
      }} initial={{
        opacity: 0,
        scale: 0.7,
        y: 12
      }} whileInView={{
        opacity: 1,
        scale: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3 + pill.delay,
        type: 'spring',
        stiffness: 240,
        damping: 18
      }} data-id="element-627">
            <motion.span className={`block whitespace-nowrap px-5 py-2.5 sm:px-6 sm:py-3 rounded-full text-base sm:text-lg font-bold text-white shadow-[0_16px_34px_-12px_rgba(43,33,26,0.45)] ${pill.bg}`} style={{
          rotate: `${pill.rotate}deg`
        }} animate={reduced ? undefined : {
          y: [0, -5, 0]
        }} transition={reduced ? undefined : {
          duration: 4 + pill.delay * 4,
          repeat: Infinity,
          ease: 'easeInOut'
        }} data-id="element-628">
              {pill.text}
            </motion.span>
          </motion.span>)}
      </div>
    </div>;
}
// "Follows through" visual — a continuous left→right carousel of REAL-LOOKING
// deliverable screenshots (a spreadsheet, an analytics chart, a finished-task
// toast, a generated PDF report, and a built slide deck). Each is a tidy little
// app window so the row reads like a stream of finished work drifting by, which
// feels livelier than the old fanned, slowly-floating stack.
function WindowDots() {
  return <span className="flex items-center gap-1 shrink-0" data-id="element-629">
      <span className="w-1.5 h-1.5 rounded-full bg-[#FF5F57]" data-id="element-630" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#FEBC2E]" data-id="element-631" />
      <span className="w-1.5 h-1.5 rounded-full bg-[#28C840]" data-id="element-632" />
    </span>;
}
// Each deliverable card is a fixed-size little app window so the carousel row
// stays even. Built once and reused across the marquee loop.
const FT_CARD_BASE = 'w-[156px] h-[176px] shrink-0 rounded-xl bg-white border border-neutral-200 shadow-[0_18px_40px_-18px_rgba(43,33,26,0.35)] overflow-hidden flex flex-col';
// "An email sent" — a sent-mail confirmation. Reads instantly as a concrete,
// finished task: Giga wrote and sent a real email on your behalf.
function EmailCard() {
  return <div className={FT_CARD_BASE} data-id="element-633">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#EA580C] shrink-0" data-id="element-634">
        <MailIcon size={10} className="text-white shrink-0" strokeWidth={2.5} data-id="element-635" />
        <span className="text-[8px] font-bold text-white truncate" data-id="element-636">
          Email sent
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-637">
        <div className="flex items-center gap-1 text-[7px] text-neutral-400 mb-1" data-id="element-638">
          <span className="font-semibold text-neutral-500" data-id="element-639">To:</span>
          sarah@acmecorp.com
        </div>
        <div className="text-[9.5px] font-bold text-neutral-900 leading-tight mb-1.5" data-id="element-640">
          Re: Q3 invoice &amp; next steps
        </div>
        <div className="space-y-1" data-id="element-641">
          <div className="h-1 w-full rounded bg-neutral-100" data-id="element-642" />
          <div className="h-1 w-[94%] rounded bg-neutral-100" data-id="element-643" />
          <div className="h-1 w-2/3 rounded bg-neutral-100" data-id="element-644" />
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-645">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-646">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-647" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-648">
            Delivered · just now
          </span>
        </div>
      </div>
    </div>;
}
// "A blog post published" — a finished, recognizable creative deliverable.
// Giga drafted the post and pushed it live, end to end.
function BlogCard() {
  return <div className={FT_CARD_BASE} data-id="element-649">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-[#EA580C] shrink-0" data-id="element-650">
        <FileTextIcon size={10} className="text-white shrink-0" strokeWidth={2.5} data-id="element-651" />

        <span className="text-[8px] font-bold text-white truncate" data-id="element-652">
          Blog published
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-653">
        <div className="text-[9.5px] font-bold text-neutral-900 leading-tight mb-1.5" data-id="element-654">
          How we cut onboarding to 5 minutes
        </div>
        <div className="space-y-1" data-id="element-655">
          <div className="h-1 w-full rounded bg-neutral-100" data-id="element-656" />
          <div className="h-1 w-[92%] rounded bg-neutral-100" data-id="element-657" />
          <div className="h-1 w-3/4 rounded bg-neutral-100" data-id="element-658" />
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-659">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-660">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-661" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-662">
            Live on the blog
          </span>
        </div>
      </div>
    </div>;
}
function ToastCard() {
  return <div className={`${FT_CARD_BASE} justify-center px-3`} data-id="element-663">
      <span className="flex items-center justify-center w-9 h-9 rounded-full bg-[#0F9D58] shrink-0 mb-2.5" data-id="element-664">
        <CheckIcon size={18} strokeWidth={3.5} className="text-white" data-id="element-665" />
      </span>
      <div className="text-[11px] font-bold text-neutral-900 leading-tight" data-id="element-666">
        Domain connected
      </div>
      <p className="mt-1 text-[9.5px] text-neutral-500 leading-snug" data-id="element-667">
        getgiga.com is live — DNS verified and SSL issued.
      </p>
      <div className="mt-2 flex items-center gap-1" data-id="element-668">
        <span className="w-1 h-1 rounded-full bg-[#0F9D58]" data-id="element-669" />
        <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-670">
          Completed just now
        </span>
      </div>
    </div>;
}
// "Finances reconciled" — a recognizable back-office outcome: Giga matched the
// month's transactions and balanced the books, end to end.
function FinanceCard() {
  return <div className={FT_CARD_BASE} data-id="element-671">
      <div className="flex items-center justify-between px-2 py-1.5 bg-neutral-50 border-b border-neutral-100 shrink-0" data-id="element-672">
        <WindowDots data-id="element-673" />
        <span className="text-[7px] font-bold text-neutral-400" data-id="element-674">
          Bookkeeping
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-675">
        <div className="text-[10px] font-bold text-neutral-900 leading-tight mb-2" data-id="element-676">
          Finances reconciled
        </div>
        <div className="space-y-1.5" data-id="element-677">
          {[{
          label: 'Transactions matched',
          value: '142'
        }, {
          label: 'Flagged for review',
          value: '0'
        }].map(r => <div key={r.label} className="flex items-center justify-between gap-2" data-id="element-678">
              <span className="text-[8px] text-neutral-500 truncate" data-id="element-679">
                {r.label}
              </span>
              <span className="text-[8px] font-bold text-neutral-800 shrink-0" data-id="element-680">
                {r.value}
              </span>
            </div>)}
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-681">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-682">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-683" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-684">
            Balanced · August
          </span>
        </div>
      </div>
    </div>;
}
// "A meeting booked" — a calendar invite Giga scheduled and confirmed. Another
// concrete, everyday outcome people instantly understand.
function MeetingCard() {
  return <div className={FT_CARD_BASE} data-id="element-685">
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-neutral-50 border-b border-neutral-100 shrink-0" data-id="element-686">
        <CalendarCheckIcon size={10} className="text-[#EA580C] shrink-0" strokeWidth={2.5} data-id="element-687" />

        <span className="text-[8px] font-bold text-neutral-500 truncate" data-id="element-688">
          Calendar
        </span>
      </div>
      <div className="p-2.5 flex-1 flex flex-col" data-id="element-689">
        <div className="flex items-center gap-2 mb-2" data-id="element-690">
          <div className="flex flex-col items-center justify-center w-9 h-9 rounded-md bg-[#EA580C]/10 shrink-0" data-id="element-691">
            <span className="text-[6px] font-bold text-[#C2410C] uppercase leading-none" data-id="element-692">
              Thu
            </span>
            <span className="text-[13px] font-bold text-[#C2410C] leading-none" data-id="element-693">
              14
            </span>
          </div>
          <div className="min-w-0" data-id="element-694">
            <div className="text-[10px] font-bold text-neutral-900 leading-tight truncate" data-id="element-695">
              Demo with Acme
            </div>
            <div className="text-[8px] text-neutral-400" data-id="element-696">2:00 – 2:30 PM</div>
          </div>
        </div>
        <div className="flex items-center gap-1 mb-2" data-id="element-697">
          {['#EA580C', '#0F9D58', '#2B211A'].map(c => <span key={c} className="w-3.5 h-3.5 rounded-full border border-white" style={{
          backgroundColor: c
        }} data-id="element-698" />)}
          <span className="text-[7px] text-neutral-400 ml-0.5" data-id="element-699">+2 invited</span>
        </div>
        <div className="mt-auto flex items-center gap-1" data-id="element-700">
          <span className="flex items-center justify-center w-3 h-3 rounded-full bg-[#0F9D58] shrink-0" data-id="element-701">
            <CheckIcon size={8} strokeWidth={4} className="text-white" data-id="element-702" />
          </span>
          <span className="text-[8px] font-semibold text-[#0F9D58]" data-id="element-703">
            Invites sent
          </span>
        </div>
      </div>
    </div>;
}
function FollowsThroughVisual() {
  const reduced = !!useReducedMotion();
  const cards = [<EmailCard key="email" data-id="element-704" />, <BlogCard key="blog" data-id="element-705" />, <ToastCard key="toast" data-id="element-706" />, <FinanceCard key="finance" data-id="element-707" />, <MeetingCard key="meeting" data-id="element-708" />];
  return <div className="flex-1 relative mt-6 overflow-hidden flex items-center" style={{
    WebkitMaskImage: 'linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%)',
    maskImage: 'linear-gradient(to right, transparent 0, #000 4%, #000 96%, transparent 100%)'
  }} data-id="element-709">
      {reduced ?
    // Reduced motion: a static, horizontally-scrollable row.
    <div className="flex gap-4 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-710">
          {cards}
        </div> :
    // Continuous left→right marquee: the track holds two identical sets and
    // animates from -50% to 0% so finished-work cards drift rightward in a
    // seamless loop.
    <motion.div className="flex gap-4 w-max" animate={{
      x: ['-50%', '0%']
    }} transition={{
      duration: 26,
      repeat: Infinity,
      ease: 'linear'
    }} data-id="element-711">
          {[...cards, ...cards].map((card, i) => cloneElement(card, {
        key: `ft-${i}`
      }))}
        </motion.div>}
    </div>;
}
// "Remembers and repeats" visual — recurring routines Giga runs on a schedule.
// A small stack of "automation" cards, each with a cadence chip (Daily / Mon),
// an ON toggle, and the deliverable it produces — with a third routine peeking
// faintly behind the stack to imply many more running quietly in the background.
const SCHEDULES = [{
  cadence: 'Daily',
  title: 'Daily Sales digest',
  desc: 'New deals, stalls, and what to chase today',
  delay: 0
}, {
  cadence: 'Mon',
  title: 'Weekly Investor Update',
  desc: 'Revenue, burn, pipeline → a clear email draft',
  delay: 0.12
}];
// A small "on" pill toggle — knob pinned right, green track — reading instantly
// as an enabled, recurring automation.
function ScheduleToggle() {
  return <span role="img" aria-label="On" className="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full bg-[#0F9D58]" data-id="element-712">
      <span className="absolute right-0.5 h-4 w-4 rounded-full bg-white shadow-sm" data-id="element-713" />
    </span>;
}
function ScheduleCard({
  s
}: {
  s: (typeof SCHEDULES)[number];
}) {
  const reduced = !!useReducedMotion();
  return <motion.div className="relative w-full rounded-2xl border border-neutral-200/60 bg-white p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.08)] transition-all duration-300 cursor-default" initial={reduced ? false : {
    opacity: 0,
    y: 14
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true
  }} transition={reduced ? {
    duration: 0.2
  } : {
    type: 'spring',
    stiffness: 240,
    damping: 22,
    delay: 0.1 + s.delay
  }} data-id="element-714">
      <div className="flex items-center justify-between mb-3" data-id="element-715">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-[#EA580C]/10 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-[#C2410C]" data-id="element-716">
          <RepeatIcon size={12} strokeWidth={2.5} data-id="element-717" />
          {s.cadence}
        </span>
        <ScheduleToggle data-id="element-718" />
      </div>
      <div className="text-sm font-bold text-neutral-900 leading-tight" data-id="element-719">
        {s.title}
      </div>
      <p className="mt-1 text-xs text-neutral-500 leading-snug" data-id="element-720">{s.desc}</p>
    </motion.div>;
}
function RemembersVisual() {
  return <div className="flex-1 w-full mt-4 flex items-center justify-center" data-id="element-721">
      <div className="w-full max-w-[300px] flex flex-col gap-3 px-1" data-id="element-722">
        {SCHEDULES.map(s => <ScheduleCard key={s.title} s={s} data-id="element-723" />)}
      </div>
    </div>;
}
// Card 1 copy — the lead line "Does the work you" stays put while the closing
// clause auto-rotates through the three framings of the work Giga takes on, and
// the supporting body copy swaps in sync with each. One source of truth so the
// heading clause and its paragraph can never drift apart.
const ROTATING_WORK = [{
  lead: "can't get to",
  body: 'Giga lives in Slack and soon Teams, your messaging apps, and your email.'
}, {
  lead: "shouldn't have to do",
  body: 'The repetitive busywork that fills your day — chased down, updated, and closed out without you.'
}, {
  lead: "can't justify hiring for",
  body: 'The work that never warranted a new headcount, handled on demand the moment it comes up.'
}];
function WorkYouCantGetToCopy() {
  const reduced = !!useReducedMotion();
  const [i, setI] = useState(0);
  useEffect(() => {
    if (reduced) return;
    const id = setInterval(() => {
      setI(p => (p + 1) % ROTATING_WORK.length);
    }, 3200);
    return () => clearInterval(id);
  }, [reduced]);
  const current = ROTATING_WORK[i];
  return <div className="flex-1 text-center md:text-left" data-id="element-724">
      <h3 className="text-xl sm:text-2xl md:text-3xl font-title font-medium text-[#2B211A] mb-3 leading-[1.14]" data-id="element-725">
        <span className="block" data-id="element-726">Giga does the work you</span>
        {/* Rotating clause — clipped so each phrase slides up out of the line
         and the next slides in, reading as a single shifting headline. The box
         is a touch taller than the line so descenders (g, y) aren't clipped. */}
        <span className="relative block h-[1.45em] overflow-hidden" data-id="element-727">
          <AnimatePresence mode="wait" initial={false} data-id="element-728">
            <motion.span key={current.lead} className="absolute inset-x-0 md:inset-x-auto md:left-0 block whitespace-nowrap leading-[1.2] text-[#EA580C]" initial={reduced ? false : {
            y: '110%',
            opacity: 0
          }} animate={{
            y: '0%',
            opacity: 1
          }} exit={reduced ? {
            opacity: 0
          } : {
            y: '-110%',
            opacity: 0
          }} transition={reduced ? {
            duration: 0.2
          } : {
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} data-id="element-729">
              {current.lead}
            </motion.span>
          </AnimatePresence>
        </span>
      </h3>
    </div>;
}
export function BenefitsBento() {
  return <section className="w-full bg-[#F4F0EA] py-24 md:py-32 border-y border-neutral-200/70 overflow-hidden" data-id="element-730">
      <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-731">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20" data-id="element-732">
          {/* Eyebrow — a pulsing live dot next to a status label, signaling
           the agent is active and ready to be deployed. */}
          <span className="inline-flex items-center gap-2.5 mb-5 md:mb-6 rounded-full border border-[#EA580C]/20 bg-[#EA580C]/[0.06] pl-2.5 pr-3.5 py-1.5" data-id="element-733">
            <span className="relative flex h-2 w-2 shrink-0" data-id="element-734">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#EA580C] opacity-75" data-id="element-735" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-[#EA580C]" data-id="element-736" />
            </span>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#C2410C]" data-id="element-737">
              Ready to start
            </span>
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium text-[#2B211A] tracking-tight leading-[1.1] mb-5 md:mb-6" data-id="element-738">
            Meet your most capable hire
          </h2>
          <p className="text-lg md:text-xl text-neutral-500 leading-relaxed max-w-2xl mx-auto" data-id="element-739">
            A proactive multiplayer agent that connects to all your tools, works
            in your workspace, and executes with all the context.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto" data-id="element-740">
          {/* Card 1: Works where you already are.
           Layout: headline pinned HIGH on the left; the task carousel runs
           as a full-width band BENEATH the headline (so they never overlap);
           the phone sits on the right, above the carousel. */}
          <div className="relative md:col-span-2 bg-[#FAF8F4] rounded-[2rem] border border-neutral-200/60 shadow-sm overflow-hidden h-[420px] sm:h-[460px] md:h-[480px]" data-id="element-741">
            {/* Carousel band — full edge-to-edge width, lower portion */}
            <div className="absolute inset-x-0 bottom-0 top-[136px] sm:top-[152px] md:top-[170px] z-0" data-id="element-742">
              <Card1Background data-id="element-743" />
            </div>

            {/* Headline — pinned high, top-left (top-center on mobile). Widened
             so the longer line and rotating clause are never clipped. */}
            <div className="absolute top-7 sm:top-9 md:top-12 inset-x-6 sm:inset-x-8 md:left-12 md:right-auto md:max-w-lg z-20" data-id="element-744">
              <WorkYouCantGetToCopy data-id="element-745" />
            </div>

            {/* Phone — anchored bottom-right, above the carousel */}
            <div className="absolute inset-y-0 right-0 w-[58%] sm:w-[52%] md:w-[46%] z-10 pointer-events-none" data-id="element-746">
              <WorksWhereYouWorkVisual data-id="element-747" />
            </div>
          </div>

          {/* Card 2: Connects in seconds */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-748">
            <div className="text-center md:text-left relative z-20" data-id="element-749">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-750">
                Self onboards in seconds
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-751">
                Once connected, Giga gets up to speed.
              </p>
            </div>
            <ConnectsVisual data-id="element-752" />
          </div>

          {/* Card 3: Acts like a partner */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-753">
            <div className="text-center md:text-left relative z-20" data-id="element-754">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-755">
                Acts on your behalf
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-756">
                Giga doesn't just reply — it does the work and hands back real
                results.
              </p>
            </div>
            <PartnerVisual data-id="element-757" />
          </div>

          {/* Card 4: Follows through */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-758">
            <div className="text-center md:text-left relative z-20" data-id="element-759">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-760">
                Never out of office
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-761">
                A colleague who's always on — replying, nudging, and noticing,
                even when you're not.
              </p>
            </div>
            <FollowsThroughVisual data-id="element-762" />
          </div>

          {/* Card 5: Remembers everything */}
          <div className="bg-[#FAF8F4] rounded-[2rem] p-6 sm:p-8 border border-neutral-200/60 shadow-sm flex flex-col overflow-hidden h-[340px] sm:h-[380px]" data-id="element-763">
            <div className="text-center md:text-left relative z-20" data-id="element-764">
              <h3 className="text-xl sm:text-2xl font-title font-medium text-[#2B211A] mb-3" data-id="element-765">
                Remembers and repeats
              </h3>
              <p className="text-neutral-500 text-sm sm:text-base" data-id="element-766">
                Set the routine once. Giga remembers it and delivers on schedule
                — without being asked twice.
              </p>
            </div>
            <RemembersVisual data-id="element-767" />
          </div>
        </div>
      </div>
    </section>;
}